import { useCallback, useEffect, useRef, useState } from 'react';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { useU2000AuthContext } from '../../u2000Core/context/auth/U2000UseAuthContext';
import { doGetAiUsage } from '../U2106Service';

type CacheEntry = {
  key: string;
  hasAiProviders: boolean;
};

/** Cache session en mémoire (org + user) pour le gate menu « Mon utilisation IA ». */
let sessionCache: CacheEntry | null = null;

export function u2106ClearHasAiProvidersCache(): void {
  sessionCache = null;
}

export type UseU2106HasAiProvidersReturn = {
  /** null = pas encore résolu ; true/false = résultat GET. */
  hasAiProviders: boolean | null;
  loading: boolean;
  ensureLoaded: () => void;
};

/**
 * Charge une fois (par org+user) le flag providers via GET /U2106/aiUsage.
 * Utilisé pour masquer l'entrée menu s'il n'y a aucun fournisseur accessible.
 */
export function useU2106HasAiProviders(enabled: boolean): UseU2106HasAiProvidersReturn {
  const axios = useU2000AxiosAuthenticated();
  const { auth } = useU2000AuthContext();
  const orgId = auth.user.organizationId ?? '';
  const userName = auth.user.userName ?? '';
  const cacheKey = `${orgId}|${userName}`;

  const [hasAiProviders, setHasAiProviders] = useState<boolean | null>(() => {
    if (sessionCache?.key === cacheKey) {
      return sessionCache.hasAiProviders;
    }
    return null;
  });
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    if (sessionCache?.key === cacheKey) {
      setHasAiProviders(sessionCache.hasAiProviders);
      return;
    }
    setHasAiProviders(null);
  }, [cacheKey]);

  const ensureLoaded = useCallback(() => {
    if (!enabled || !auth.isAuthenticated || !orgId) {
      return;
    }
    if (sessionCache?.key === cacheKey) {
      setHasAiProviders(sessionCache.hasAiProviders);
      return;
    }
    if (inFlight.current) {
      return;
    }

    inFlight.current = true;
    setLoading(true);
    doGetAiUsage(axios)
      .then((response) => {
        const value = (response.data.result.providers?.length ?? 0) > 0;
        sessionCache = { key: cacheKey, hasAiProviders: value };
        setHasAiProviders(value);
      })
      .catch(() => {
        sessionCache = { key: cacheKey, hasAiProviders: false };
        setHasAiProviders(false);
      })
      .finally(() => {
        inFlight.current = false;
        setLoading(false);
      });
  }, [enabled, auth.isAuthenticated, orgId, cacheKey, axios]);

  return { hasAiProviders, loading, ensureLoaded };
}
